import {useMemo, useState, useEffect} from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import type {Tab as PageControlBarTab} from 'shells/control-bar/page-control-bar';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIconChevron} from 'shells/icon/svg/lead-icon-chevron';

import {PageContainer} from '../../layout';

import {CreateOrEditModal} from './create-or-edit-modal/create-or-edit-modal';
import {
    CustomFields,
    LEAD_TAB_NAME,
    COMPANY_TAB_NAME,
    PERSON_TAB_NAME,
    TAB_GRAPHQL_MAP,
    LEAD_VIEW_TAB_NAME,
    COMPANY_VIEW_TAB_NAME,
    PERSON_VIEW_TAB_NAME,
} from './custom-fields';
import {
    useArchiveCustomField,
    useGetCustomFieldStagesets,
    useUnarchiveCustomField,
} from './graphql/hooks';

type Props = ContextRouter;

const TAB_TITLE_MAP = {
    [LEAD_TAB_NAME]: 'Lead custom fields',
    [COMPANY_TAB_NAME]: 'Company custom fields',
    [PERSON_TAB_NAME]: 'Person custom fields',
} as const;

export function CustomFieldsPage(props: Props) {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isArchiveConfirmationDialogOpen, setIsArchiveConfirmationDialogOpen] =
        useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [fieldId, setFieldId] = useState<string | null>(null);

    const {stagesets, refetch: refetchStagesets} = useGetCustomFieldStagesets();
    const {archiveCustomField} = useArchiveCustomField();
    const {unarchiveCustomField} = useUnarchiveCustomField();

    const handleOpenCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const handleOpenEditModal = (editFieldId: string) => {
        setFieldId(editFieldId);
        setIsEditing(true);
        setIsCreateModalOpen(true);
    };

    const handleArchiveConfirmation = (fieldIdToArchive: string) => {
        setFieldId(fieldIdToArchive);
        setIsArchiveConfirmationDialogOpen(true);
    };

    const handleArchive = () => {
        setIsArchiveConfirmationDialogOpen(false);
        if (!fieldId) return;
        // @ts-expect-error
        archiveCustomField({id: fieldId}, TAB_GRAPHQL_MAP[tab]);
    };

    const handleRestore = (fieldIdToRestore: string) => {
        // @ts-expect-error
        unarchiveCustomField({id: fieldIdToRestore}, TAB_GRAPHQL_MAP[tab]).then(() => {
            if (tab === LEAD_TAB_NAME) {
                refetchStagesets();
            }
        });
    };

    const tab = props.match.params.tab || LEAD_TAB_NAME;
    // @ts-expect-error
    const title = TAB_TITLE_MAP[tab];

    const searchParams = useMemo(
        () => new URLSearchParams(props.history.location.search),
        [props.history.location.search]
    );
    const archived = searchParams.get('archived') === 'true';
    const selectedPipelineId = searchParams.get('pipeline');
    const shouldOpenCreateModal = searchParams.get('create') !== null;

    useEffect(() => {
        if (shouldOpenCreateModal && !isCreateModalOpen) {
            setIsCreateModalOpen(true);
            searchParams.delete('create');
            props.history.replace({
                pathname: props.history.location.pathname,
                search: searchParams.toString(),
            });
        }
    }, [isCreateModalOpen, props.history, searchParams, shouldOpenCreateModal]);

    const tabs: PageControlBarTab[] = useMemo(
        () => [
            {
                icon: AccountIcon,
                label: COMPANY_VIEW_TAB_NAME,
                id: COMPANY_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/custom-field/${COMPANY_TAB_NAME}`,
            },
            {
                icon: ContactIcon,
                label: PERSON_VIEW_TAB_NAME,
                id: PERSON_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/custom-field/${PERSON_TAB_NAME}`,
            },
            {
                icon: LeadIconChevron,
                label: LEAD_VIEW_TAB_NAME,
                id: LEAD_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/custom-field/${LEAD_TAB_NAME}`,
            },
        ],
        []
    );

    return (
        <PageContainer
            title={title}
            whiteBackground={true}
            controlBar={<PageControlBar tabs={{options: tabs, selectedTab: tab}} />}
        >
            <CustomFields
                tab={tab}
                isViewingArchived={archived}
                selectedPipelineId={selectedPipelineId}
                pipelines={stagesets}
                onOpenCreateModal={handleOpenCreateModal}
                onOpenEditModal={handleOpenEditModal}
                onArchive={handleArchiveConfirmation}
                onRestore={handleRestore}
            />
            {isCreateModalOpen && (
                <CreateOrEditModal
                    onClose={() => {
                        setIsCreateModalOpen(false);
                        setIsEditing(false);
                        setFieldId(null);
                    }}
                    entityType={tab}
                    isEditing={isEditing}
                    fieldId={fieldId}
                    refetchStagesets={refetchStagesets}
                />
            )}
            {isArchiveConfirmationDialogOpen && (
                <ConfirmationDialog
                    title='Are you sure you want to archive this custom field?'
                    message='This will remove it from any pipelines or forms where it is currently being used.'
                    onConfirm={() => handleArchive()}
                    onCancel={() => setIsArchiveConfirmationDialogOpen(false)}
                />
            )}
        </PageContainer>
    );
}
